import { FC, useMemo } from 'react';
import Handlebars from 'handlebars';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { getDateChangeEmailTemplate } from 'services/EventNotifications';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import styles from './PunchupNotifyDateChangeDialog.module.css';

/** Handlebars variables — one-to-one with the keys the backend emits in NotifyEventDateChange. */
export interface DateChangeEmailData {
	eventName: string;
	eventId?: string;
	eventStartDate?: string;
	eventStartTime?: string;
	eventEndDate?: string;
	eventEndTime?: string;
	eventTimeZone?: string;
	venueName?: string;
	companyLogoUrl?: string;
	eventBanner?: string;
	primaryColor?: string;
	titleColor?: string;
	webpageUrl?: string;
	customMessage?: string;
}

interface DateChangeEmailPreviewProps {
	data: DateChangeEmailData;
}

// Renders a faithful preview of the exact email buyers receive: fetches the raw template HTML from the
// backend (the same file the send path uses), compiles it with handlebars + the live form data, and
// shows it in a sandboxed iframe so the email's own styles are fully isolated from the app.
export const DateChangeEmailPreview: FC<DateChangeEmailPreviewProps> = ({ data }) => {
	const { t } = useTranslation();

	const {
		data: templateHtml,
		isLoading,
		isError,
	} = useQuery({
		queryKey: ['date-change-email-template'],
		queryFn: getDateChangeEmailTemplate,
		staleTime: Infinity, // the template rarely changes; fetch once per session
	});

	const compiledHtml = useMemo(() => {
		if (!templateHtml) return '';
		try {
			return Handlebars.compile(templateHtml)(data);
		} catch {
			return '';
		}
	}, [templateHtml, data]);

	if (isLoading) {
		return (
			<div className={styles.previewLoading}>
				<PunchupSpinner size={24} />
			</div>
		);
	}

	if (isError || !compiledHtml) {
		return <p className={styles.previewError}>{t('editEvent-notify-preview-error', 'Preview unavailable')}</p>;
	}

	return (
		<iframe
			className={styles.previewFrame}
			srcDoc={compiledHtml}
			title={t('editEvent-notify-preview-label', 'Preview')}
			sandbox=""
		/>
	);
};
